import { cn } from "@/lib/utils";

export type KnowledgeHealthFilter = "connected" | "not-connected" | "stale" | "never-used";

export interface KnowledgeHealthCountersProps {
  counts: {
    connected: number;
    notConnected: number;
    stale: number;
    neverUsed: number;
  };
  active: KnowledgeHealthFilter | null;
  onChange: (filter: KnowledgeHealthFilter | null) => void;
  /**
   * False when "never used" cannot be measured yet (no usage tracking wired
   * up). Renders that tile disabled with an honest "Not measured yet" label
   * instead of a fabricated count. Defaults to true.
   */
  neverUsedAvailable?: boolean;
}

interface CounterTile {
  key: KnowledgeHealthFilter;
  label: string;
  count: number;
  available: boolean;
}

/**
 * Four counters that double as filter toggles for the document library
 * below them. Replaces the old "Ready to use" / "Needs attention" reassurance
 * cards, which glossed over zero counts with cheerful copy ("Everything is
 * connected and ready"). Here a zero renders as a plain "0" — still
 * clickable, just visually muted — and any signal we can't actually measure
 * yet says so instead of guessing.
 */
export const KnowledgeHealthCounters = ({
  counts,
  active,
  onChange,
  neverUsedAvailable = true,
}: KnowledgeHealthCountersProps) => {
  const tiles: CounterTile[] = [
    { key: "connected", label: "In the Genie", count: counts.connected, available: true },
    { key: "not-connected", label: "Not connected", count: counts.notConnected, available: true },
    { key: "stale", label: "Older than 6 months", count: counts.stale, available: true },
    {
      key: "never-used",
      label: "Never used",
      count: counts.neverUsed,
      available: neverUsedAvailable,
    },
  ];

  const handleToggle = (tile: CounterTile) => {
    if (!tile.available) return;
    onChange(active === tile.key ? null : tile.key);
  };

  return (
    <div
      className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4"
      role="group"
      aria-label="Filter sources by knowledge health"
    >
      {tiles.map((tile) => {
        const isActive = tile.available && active === tile.key;
        const isZero = tile.available && tile.count === 0;

        return (
          <button
            key={tile.key}
            type="button"
            onClick={() => handleToggle(tile)}
            disabled={!tile.available}
            aria-pressed={tile.available ? isActive : undefined}
            className={cn(
              "rounded-xl border bg-white px-4 py-3 text-left transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2",
              !tile.available && "cursor-not-allowed border-gray-200 opacity-60",
              tile.available &&
                !isActive &&
                "border-gray-200 hover:border-genie-primary/30 hover:shadow-md",
              isActive && "border-genie-primary bg-genie-primary/5 ring-2 ring-genie-primary",
            )}
          >
            <div
              className={cn(
                "text-2xl font-bold tabular-nums",
                !tile.available || isZero ? "text-gray-400" : "text-gray-900",
              )}
            >
              {tile.available ? tile.count : "—"}
            </div>
            <div className="mt-1 text-xs font-medium text-gray-600">{tile.label}</div>
            {!tile.available && (
              <div className="mt-0.5 text-[11px] text-gray-400">Not measured yet</div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default KnowledgeHealthCounters;
